import { useRef, useState } from "react";
import { Upload, Loader2, X } from "lucide-react";
import api, { API_BASE } from "../lib/api";
import axios from "axios";
import { resolveAssetUrl } from "../lib/assets";

/**
 * Generic file uploader. Renders either an image dropzone (when `accept` is image/*)
 * or a button uploader. Calls `onUploaded(record)` with the stored file metadata.
 *
 *   endpoint      backend path under /api (e.g. "/uploads/profile-photo")
 *   accept        MIME accept list (e.g. "image/jpeg,image/png,image/webp")
 *   maxBytes      reject larger files client-side with friendly message
 *   public        when true, fire without auth (used for onboarding form)
 *   previewUrl    existing image url to show as preview
 *   variant       "avatar" | "document"
 *   buttonLabel   label for the document variant trigger
 */
export default function FileUpload({
  endpoint,
  accept = "image/jpeg,image/png,image/webp",
  maxBytes = 5 * 1024 * 1024,
  public: isPublic = false,
  previewUrl = "",
  variant = "avatar",
  buttonLabel = "Upload file",
  onUploaded,
  dataTestid,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [localPreview, setLocalPreview] = useState("");

  const trigger = () => inputRef.current?.click();

  const handle = async (e) => {
    setError("");
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading same filename
    if (!file) return;
    if (file.size > maxBytes) {
      setError(`File too large (max ${(maxBytes / 1024 / 1024).toFixed(0)} MB).`);
      return;
    }
    setBusy(true);
    if (file.type.startsWith("image/")) {
      setLocalPreview(URL.createObjectURL(file));
    }
    const form = new FormData();
    form.append("file", file);
    try {
      const url = `${API_BASE}${endpoint}`;
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      const client = isPublic ? axios : api;
      const { data } = isPublic
        ? await axios.post(url, form, config)
        : await client.post(endpoint, form, config);
      onUploaded?.(data);
    } catch (ex) {
      const detail =
        ex?.response?.data?.detail ||
        (typeof ex?.message === "string" ? ex.message : "Upload failed.");
      setError(typeof detail === "string" ? detail : "Upload failed.");
      setLocalPreview("");
    } finally {
      setBusy(false);
    }
  };

  const shownPreview = localPreview || resolveAssetUrl(previewUrl);

  if (variant === "avatar") {
    return (
      <div className="flex items-center gap-5" data-testid={dataTestid}>
        <button
          type="button"
          onClick={trigger}
          disabled={busy}
          className="relative w-24 h-24 rounded-full border border-[#C9A84C]/40 hover:border-[#C9A84C] bg-[#1A1A1A] flex items-center justify-center overflow-hidden transition disabled:opacity-60"
          data-testid={`${dataTestid || "avatar"}-trigger`}
        >
          {shownPreview ? (
            <img src={shownPreview} alt="" className="w-full h-full object-cover" />
          ) : (
            <Upload size={22} className="text-[#C9A84C]" />
          )}
          {busy && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <Loader2 size={22} className="animate-spin text-[#C9A84C]" />
            </div>
          )}
        </button>
        <div className="space-y-1">
          <button
            type="button"
            onClick={trigger}
            disabled={busy}
            className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] hover:text-[#E1C97A] transition disabled:opacity-50"
          >
            {shownPreview ? "Change photo" : "Upload photo"}
          </button>
          <div className="text-[10px] uppercase tracking-widest text-[#9A9590]/70">
            JPG · PNG · WEBP · max {(maxBytes / 1024 / 1024).toFixed(0)} MB
          </div>
          {error && <div className="text-xs text-red-400/90">{error}</div>}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handle}
          className="hidden"
          data-testid={`${dataTestid || "avatar"}-input`}
        />
      </div>
    );
  }

  // document variant
  return (
    <div data-testid={dataTestid}>
      <button
        type="button"
        onClick={trigger}
        disabled={busy}
        className="btn-ghost-gold rounded-md px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-60"
        data-testid={`${dataTestid || "doc"}-trigger`}
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        {busy ? "Uploading…" : buttonLabel}
      </button>
      {error && <div className="text-xs text-red-400/90 mt-2">{error}</div>}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handle}
        className="hidden"
        data-testid={`${dataTestid || "doc"}-input`}
      />
    </div>
  );
}

export function ClearButton({ onClick, label = "Remove", testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs text-[#9A9590] hover:text-red-400 inline-flex items-center gap-1"
      data-testid={testid}
    >
      <X size={12} /> {label}
    </button>
  );
}
